// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
/**
 * session.ts — host authority, the clock, and the host takeover.
 *
 * Host-authoritative star (genuine hidden info):
 *   host  -> all : 'snap' public state (no hand identities, no in-flight true type)
 *   host  -> one : 'priv' your hand + the in-flight true type IF you have seen it
 *   peer  -> host: 'act'  play / guess / peekStart / peekPass
 *   host* -> all : 'htake' new host asks everyone to re-attest their OWN hand
 *   peer  -> host*: 'hgive' my hand (+ the in-flight card's type, if I'm its origin)
 *
 * Why re-attest works here without an undead draw pile: Cockroach-style play deals
 * the WHOLE deck into hands and cards only move hand -> menagerie, so there is no
 * hidden draw pile to lose. The only secrets are the hands (each peer keeps its own,
 * so it can answer) and the one in-flight card (its origin is a seer who still knows
 * its type). The promoted host rewinds the in-flight card to its origin and rebuilds.
 *
 * The transport is injected, so every branch — including the takeover — is drivable
 * from a test with no network at all.
 */
import {
  deal,
  play,
  guess,
  beginPeek,
  peekPass,
  forfeit,
  outOfMoves,
  publicView,
  privateView,
  peekTargets,
  legalTargets,
  seatOf,
  aliveSeats,
  type GameState,
  type PublicState,
  type PrivateView,
  type Card,
  type CritterType,
  type Guess,
  type ModeRules,
  type SeatSpec,
} from './game';
import { pick, makeRng } from '@ben-gy/game-engine/rng';
import { chooseGive, chooseReceive, choosePeek, type BotProfile } from './bot';

export type Action =
  | { t: 'play'; cardId: number; to: string; claim: CritterType }
  | { t: 'guess'; g: Guess }
  | { t: 'peekStart' }
  | { t: 'peekPass'; to: string; claim: CritterType };

export const GAME_CHANNELS = {
  snap: 'snap',
  priv: 'priv',
  act: 'act',
  handRequest: 'htake',
  handReply: 'hgive',
} as const;

/** A peer's re-attested hand, plus the in-flight card's type if it is that card's origin. */
export interface HandAttest {
  hand: Card[];
  flightType: CritterType | null;
}

export interface Transport {
  sendSnap(pub: PublicState): void;
  sendPriv(to: string, priv: PrivateView): void;
  sendAct(a: Action): void;
  requestHands(): void;
  sendHand(to: string, attest: HandAttest): void;
}

export interface SessionOpts {
  selfId: string;
  solo: boolean;
  transport?: Transport;
  /** Bot profiles by seat id (solo only). */
  bots?: Map<string, BotProfile>;
  roster?: readonly string[];
  rules?: ModeRules;
  onPublic: (pub: PublicState) => void;
  onPrivate: (priv: PrivateView | null) => void;
  onFlash?: (msg: string) => void;
  onStranded?: () => void;
  now?: () => number;
  /** Solo bot think time, ms — makes moves watchable. */
  botDelayMs?: number;
}

const ROLE_TIMEOUT_MS = 4_000;
const noopTransport: Transport = {
  sendSnap() {},
  sendPriv() {},
  sendAct() {},
  requestHands() {},
  sendHand() {},
};

export class Session {
  readonly selfId: string;
  private solo: boolean;
  private tx: Transport;
  private opts: SessionOpts;
  private now: () => number;
  private roster: readonly string[];
  private bots: Map<string, BotProfile>;
  private botDelay: number;

  private state: GameState | null = null;
  private pub: PublicState | null = null;
  private priv: PrivateView | null = null;
  private isHost = false;

  private actorStartedAt = 0;
  private lastActor: string | null = null;
  private botActAt = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  // Takeover bookkeeping.
  private awaitingHands = false;
  private handReplies = new Map<string, HandAttest>();
  private handDeadline = 0;
  private goneIds = new Set<string>();

  constructor(opts: SessionOpts) {
    this.opts = opts;
    this.selfId = opts.selfId;
    this.solo = opts.solo;
    this.tx = opts.transport ?? noopTransport;
    this.now = opts.now ?? (() => Date.now());
    this.roster = opts.roster ?? [];
    this.bots = opts.bots ?? new Map();
    this.botDelay = opts.botDelayMs ?? 900;
  }

  private seatIds(): string[] {
    if (this.roster.length) return [...this.roster];
    return (this.state?.seats ?? this.pub?.seats ?? []).map((s) => s.id);
  }
  private seated(): boolean {
    const ids = this.seatIds();
    return ids.length === 0 || ids.includes(this.selfId);
  }

  /** Who should run this round given who is in the room (see nightwire's note). */
  authorityFor(peers: readonly string[], host?: string | null): string | null {
    const seated = new Set(this.seatIds());
    const eligible = peers.filter((p) => seated.has(p)).sort();
    if (host && eligible.includes(host)) return host;
    return eligible[0] ?? null;
  }

  /** The seat that must act right now — a giver, or the current receiver/peeker. */
  private currentActor(s: GameState): string {
    if (!s.inFlight) return s.turn;
    return s.inFlight.peeking ?? s.inFlight.to;
  }

  // ---- lifecycle -----------------------------------------------------------

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), 200);
  }
  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
  destroy(): void {
    this.stop();
  }

  startAsHost(seed: number, specs: readonly SeatSpec[]): void {
    this.isHost = true;
    this.state = deal(seed, specs, this.opts.rules!);
    this.lastActor = this.currentActor(this.state);
    this.actorStartedAt = this.now();
    this.botActAt = this.now() + this.botDelay;
    this.publish();
    this.start();
  }

  onSnapshot(pub: PublicState): void {
    if (this.isHost) return;
    this.pub = pub;
    this.opts.onPublic(pub);
  }
  onPrivate(priv: PrivateView): void {
    this.priv = priv;
    this.opts.onPrivate(priv);
  }

  // ---- actions -------------------------------------------------------------

  act(a: Action): void {
    if (this.isHost) this.applyAction(this.selfId, a);
    else this.tx.sendAct(a);
  }
  onAction(from: string, a: Action): void {
    if (this.isHost) this.applyAction(from, a);
  }

  private applyAction(from: string, a: Action): void {
    if (!this.state || this.state.phase !== 'play') return;
    const before = this.state;
    switch (a.t) {
      case 'play':
        this.state = play(this.state, from, a.cardId, a.to, a.claim);
        break;
      case 'guess':
        this.state = guess(this.state, from, a.g);
        break;
      case 'peekStart':
        this.state = beginPeek(this.state, from);
        break;
      case 'peekPass':
        this.state = peekPass(this.state, from, a.to, a.claim);
        break;
    }
    if (this.state !== before) this.afterChange();
  }

  private afterChange(): void {
    if (!this.state) return;
    const actor = this.currentActor(this.state);
    if (actor !== this.lastActor) {
      this.lastActor = actor;
      this.actorStartedAt = this.now();
      if (this.state.seats.find((s) => s.id === actor)?.bot) this.botActAt = this.now() + this.botDelay;
    }
    this.publish();
  }

  // ---- roster / host transfer ---------------------------------------------

  onPeerLeave(id: string): void {
    this.goneIds.add(id);
    if (this.awaitingHands && this.haveAllHands()) this.assumeControl();
    if (!this.isHost || !this.state) return;
    const seat = seatOf(this.state, id);
    if (!seat || !seat.alive) return;
    // A departed player leaves the table; their hand goes with them.
    this.state = {
      ...this.state,
      seats: this.state.seats.map((x) => (x.id === id ? { ...x, alive: false, hand: [] } : x)),
      log: [...this.state.log, { text: `${seat.name} left the table.`, kind: 'out' }],
    };
    // If they were mid-decision or it was their turn, keep the game moving.
    if (this.state.inFlight && (this.state.inFlight.to === id || this.state.inFlight.peeking === id)) {
      this.state = { ...this.state, inFlight: null, turn: this.nextAlive(id) };
    } else if (this.state.turn === id) {
      this.state = { ...this.state, turn: this.nextAlive(id) };
    }
    this.endIfDone();
    this.afterChange();
  }

  onRoster(): void {
    if (this.isHost) this.publish();
  }

  setHost(isHost: boolean): void {
    if (isHost === this.isHost) return;
    if (!isHost) {
      this.isHost = false;
      this.state = null;
      return;
    }
    if (!this.seated()) return;
    this.isHost = true;
    if (this.state) {
      this.publish();
      return;
    }
    if (this.pub?.phase === 'over') {
      this.opts.onFlash?.("You're the host now.");
      return;
    }
    if (!this.pub) {
      this.isHost = false;
      this.opts.onFlash?.('The host left before the table reached you — back to the lobby.');
      this.opts.onStranded?.();
      return;
    }
    this.awaitingHands = true;
    this.handReplies = new Map();
    if (this.priv) {
      this.handReplies.set(this.selfId, {
        hand: this.priv.hand,
        flightType: this.pub.inFlight?.origin === this.selfId ? this.priv.inFlightTrueType : null,
      });
    }
    this.handDeadline = this.now() + ROLE_TIMEOUT_MS;
    this.tx.requestHands();
    this.opts.onFlash?.("The host left — you're dealing now.");
    this.start();
  }

  onHandRequest(from: string): void {
    if (!this.priv || !this.pub) return;
    this.tx.sendHand(from, {
      hand: this.priv.hand,
      flightType: this.pub.inFlight?.origin === this.selfId ? this.priv.inFlightTrueType : null,
    });
  }
  onHandReply(from: string, attest: HandAttest): void {
    if (!this.awaitingHands) return;
    this.handReplies.set(from, attest);
    if (this.haveAllHands()) this.assumeControl();
  }
  private haveAllHands(): boolean {
    if (!this.pub) return false;
    return this.pub.seats
      .filter((s) => s.alive && !this.goneIds.has(s.id) && s.id !== this.selfId)
      .every((s) => this.handReplies.has(s.id));
  }

  private assumeControl(): void {
    if (!this.pub) return;
    this.awaitingHands = false;
    const pub = this.pub;

    // Anyone who never attested is treated as gone (we cannot conjure their hand).
    const attested = new Set(this.handReplies.keys());
    const state: GameState = {
      seed: pub.seed,
      rules: pub.rules,
      seats: pub.seats.map((s) => {
        const gone = !attested.has(s.id) && s.id !== this.selfId;
        const hand = this.handReplies.get(s.id)?.hand ?? (s.id === this.selfId ? this.priv?.hand ?? [] : []);
        return {
          id: s.id,
          name: s.name,
          bot: s.bot,
          hand: gone ? [] : hand.map((c) => ({ ...c })),
          menagerie: s.menagerie.map((c) => ({ ...c })),
          alive: s.alive && !gone,
          tell: { ...s.tell },
          peeksLeft: s.peeksLeft,
        };
      }),
      turn: pub.turn,
      inFlight: null,
      phase: 'play',
      loserId: null,
      ranking: [],
      log: [...pub.log, { text: 'A new host takes the deal.', kind: 'info' }],
      events: [],
      ply: pub.ply,
      lastResolve: pub.lastResolve,
    };

    // Recover the in-flight card by rewinding it to its origin's hand.
    if (pub.inFlight) {
      const originId = pub.inFlight.origin;
      const flightType = this.handReplies.get(originId)?.flightType ?? null;
      const origin = state.seats.find((x) => x.id === originId);
      if (origin && origin.alive && flightType) {
        origin.hand.push({ id: pub.inFlight.cardId, type: flightType });
        state.turn = originId;
        state.log.push({ text: 'The card in play was recalled — a fresh pass.', kind: 'info' });
      } else {
        // Its type died with a departed origin; the card is lost from the game.
        state.turn = state.seats.find((x) => x.alive)?.id ?? state.turn;
        state.log.push({ text: 'A card in play was lost when the host left.', kind: 'info' });
      }
    }
    if (!state.seats.find((x) => x.id === state.turn && x.alive)) {
      state.turn = state.seats.find((x) => x.alive)?.id ?? state.turn;
    }

    this.state = state;
    this.lastActor = this.currentActor(state);
    this.actorStartedAt = this.now();
    this.botActAt = this.now() + this.botDelay;
    this.endIfDone();
    this.publish();
  }

  private nextAlive(afterId: string): string {
    const seats = this.state!.seats;
    const start = seats.findIndex((s) => s.id === afterId);
    for (let k = 1; k <= seats.length; k++) {
      const s = seats[(start + k) % seats.length];
      if (s.alive) return s.id;
    }
    return afterId;
  }

  /**
   * End the round if the table has collapsed to a single survivor by departures.
   * This is a WALKOVER: the last player standing WINS — never punish the survivor
   * for the others quitting. The nominal loser is a player who left, and the
   * survivor is ranked first so the results say "you survived", not "you lost".
   * (A genuine threshold loss ends the game the normal way, before this fires.)
   */
  private endIfDone(): void {
    if (!this.state || this.state.phase === 'over') return;
    const alive = aliveSeats(this.state);
    if (alive.length > 1) return;
    const survivor = alive[0]?.id ?? null;
    const departed = this.state.seats.filter((s) => !s.alive).map((s) => s.id);
    const loser = this.state.loserId ?? departed[departed.length - 1] ?? survivor ?? this.state.turn;
    const rest = this.state.seats.filter((s) => s.id !== survivor).map((s) => s.id);
    this.state = {
      ...this.state,
      phase: 'over',
      loserId: loser,
      ranking: survivor ? [survivor, ...rest] : [...rest],
      log: [
        ...this.state.log,
        { text: 'Everyone else left the table — you win by walkover.', kind: 'out' },
      ],
    };
  }

  // ---- clock ---------------------------------------------------------------

  tick(): void {
    if (this.awaitingHands && this.now() >= this.handDeadline) {
      this.assumeControl();
      return;
    }
    if (!this.isHost || !this.state || this.state.phase !== 'play') return;

    if (this.solo) {
      this.runBots();
      return;
    }
    // P2P: a silent human must not hang the room — force their move on timeout.
    const rules = this.state.rules;
    if (this.now() - this.actorStartedAt >= rules.decisionMs) this.forceCurrentMove();
  }

  private runBots(): void {
    if (!this.state || this.now() < this.botActAt) return;
    const actor = this.currentActor(this.state);
    const seat = seatOf(this.state, actor);
    if (!seat || !seat.bot) return;
    this.doBotMove(actor);
    this.botActAt = this.now() + this.botDelay;
  }

  private doBotMove(id: string): void {
    const s = this.state!;
    const pub = publicView(s);
    const priv = privateView(s, id)!;
    const profile = this.bots.get(id);
    if (!profile) return;
    if (!s.inFlight) {
      if (outOfMoves(s, id)) {
        this.state = forfeit(s, id);
      } else {
        const g = chooseGive(pub, id, priv, profile);
        this.state = g ? play(s, id, g.cardId, g.to, g.claim) : forfeit(s, id);
      }
    } else {
      const targets = peekTargets(s);
      const rc = chooseReceive(pub, id, priv, profile, targets.length > 0, targets);
      if (rc.action === 'peek' && targets.length) {
        const trueType = s.inFlight.type;
        const { to, claim } = choosePeek(pub, id, trueType, profile, targets);
        this.state = peekPass(s, id, to, claim);
      } else {
        this.state = guess(s, id, rc.action === 'bluff' ? 'bluff' : 'truth');
      }
    }
    if (this.state !== s) this.afterChange();
  }

  /** P2P only: resolve a timed-out human's move so the room keeps moving. */
  private forceCurrentMove(): void {
    const s = this.state!;
    const id = this.currentActor(s);
    const rng = makeRng(`${s.seed}:${s.ply}:force:${id}`);
    if (!s.inFlight) {
      if (outOfMoves(s, id)) {
        this.state = forfeit(s, id);
      } else {
        const seat = seatOf(s, id)!;
        const card = pick(rng, seat.hand);
        const to = pick(rng, legalTargets(s, id));
        this.state = play(s, id, card.id, to, card.type); // truthful auto-play
      }
    } else if (s.inFlight.peeking === id) {
      const targets = peekTargets(s);
      if (targets.length) this.state = peekPass(s, id, pick(rng, targets), s.inFlight.type);
      else this.state = guess(s, id, 'truth');
    } else {
      this.state = guess(s, id, 'truth'); // a silent receiver believes
    }
    if (this.state !== s) this.afterChange();
  }

  // ---- publishing ----------------------------------------------------------

  private publish(): void {
    if (!this.state) return;
    const pub = publicView(this.state);
    this.pub = pub;
    this.opts.onPublic(pub);
    this.tx.sendSnap(pub);

    const mine = privateView(this.state, this.selfId);
    if (mine) {
      this.priv = mine;
      this.opts.onPrivate(mine);
    }
    for (const seat of this.state.seats) {
      if (seat.bot || seat.id === this.selfId) continue;
      const p = privateView(this.state, seat.id);
      if (p) this.tx.sendPriv(seat.id, p);
    }
  }

  // ---- introspection -------------------------------------------------------

  hosting(): boolean {
    return this.isHost;
  }
  publicState(): PublicState | null {
    return this.pub;
  }
  privateState(): PrivateView | null {
    return this.priv;
  }
  authoritative(): GameState | null {
    return this.state;
  }
  /** Ms the current actor has left before an auto-move (P2P), or null in solo. */
  actorDeadlineMs(): number | null {
    if (this.solo || !this.state || this.state.phase !== 'play') return null;
    return Math.max(0, this.state.rules.decisionMs - (this.now() - this.actorStartedAt));
  }
}
